<template>
    <div
        class="app-input"
        :class="{ 'label-is-up': filled, 'has-error': error }"
    >
        <label :id="label">
            {{ label | __ }}
        </label>

        <input
            :type="type"
            v-model="newValue"
            :id="label"
            :readonly="readonly"
            :autofocus="autofocus"
            :disabled="disabled || (disableOnLoading && loading)"
            :autocomplete="autocomplete"
            :placeholder="placeholderTrans"
            @focus="$emit('focus', $event)"
            @blur="$emit('blur', $event)"
            @input="onInput"
        />

        <div class="loader" v-if="loading" v-html="$icon('loader')"></div>

        <div class="tooltip" v-if="tooltip">
            <span class="tooltip-icon" v-html="$icon('info')"></span>
            <div class="tooltip-message" v-html="tooltip"></div>
        </div>

        <p v-if="message" class="message">
            {{ message }}
        </p>
    </div>
</template>

<script>
export default {
    name: "app-input",

    props: {
        type: {
            type: String,
            default: "text"
        },
        value: [String, Number],
        label: String,
        placeholder: String,
        error: Boolean,
        message: String,
        autofocus: Boolean,
        readonly: Boolean,
        autocomplete: {
            type: [Boolean, String],
            default: true
        },
        tooltip: {
            type: [Boolean, String],
            default: false
        },
        loading: false,
        disabled: false,
        disableOnLoading: true
    },

    data() {
        return {
            newValue: this.value,
            focused: false
        };
    },

    watch: {
        value() {
            this.newValue = this.value;
            if (this.type === "number" && this.newValue < 0)
                this.newValue = Math.abs(this.newValue);
        }
    },

    methods: {
        onInput() {
            this.$emit("input", this.newValue);
            this.$emit("change", this.newValue);
        }
    },

    computed: {
        filled() {
            return this.newValue || this.focused;
        },

        placeholderTrans() {
            return this.placeholder ? this.$trans(this.placeholder) : "";
        }
    }
};
</script>

<style lang="scss" scoped>
.app-input {
    display: block;
    position: relative;
    font-size: 25px;
    font-weight: $font-light;
    line-height: 0.8;
    color: $black;

    input {
        position: relative;
        z-index: 1;
        display: block;
        width: 100%;
        font-size: 20px;
        font-weight: $font-light;
        color: $black;
        outline: none;
        height: 50px;
        padding: 15px 33px;
        border-radius: 100px;
        border: 1px solid rgba(33, 67, 146, 0.2);
        background: $lightest-gray;
    }

    input::-webkit-inner-spin-button,
    input::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    label {
        color: rgba(0, 0, 0, 0.6);
        text-align: right;
        font-size: 15px;
        font-weight: bold;
        display: block;
        margin-bottom: 10px;
    }

    .loader {
        position: absolute;
        width: 1em;
        height: 1em;
        left: 15px;
        top: 33px;
        z-index: 3;

        ::v-deep svg {
            stroke: black;
        }
    }

    .tooltip {
        position: absolute;
        width: 1em;
        height: 1em;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        cursor: pointer;

        ::v-deep svg {
            stroke: black;
        }

        .tooltip-icon {
            &:hover {
                + .tooltip-message {
                    display: block;
                }
            }
        }

        .tooltip-message {
            display: none;
            position: absolute;
            right: 35px;
            top: 50%;
            transform: translateY(-50%);
            background: #eee;
            border-radius: 5px;
            padding: 5px;
            font-size: 16px;
            min-width: 130px;
            box-shadow: 5px 4px 12px rgba(0, 0, 0, 0.2);

            &:before {
                position: absolute;
                content: "";
                right: -10px;
                width: 0;
                height: 0;
                border-top: 10px solid transparent;
                border-bottom: 10px solid transparent;
                border-left: 10px solid #eee;
                top: 50%;
                transform: translateY(-50%);
            }
        }
    }

    .line {
        position: absolute;
        display: block;
        left: 50%;
        width: 0;
        bottom: -1px;
        height: 1px;
        z-index: 3;
        transform: translateX(-50%);
        background-color: $primary-blue;
        transition: all 0.4s ease-in-out;
    }

    .message {
        font-size: 16px;
        margin: 10px 0 0;
        color: $error-red;
        font-weight: $font-normal;
    }
}
</style>
